/**
 * The region (D244, reshaped): the weekly goal every world's earnings count towards, the
 * bonus it pays, this world's own share of it, and the table of the region's worlds by what
 * each has earned this week (D262). Above them, the village's orders for the day and the
 * themed week (D304): the reasons to come back, kept with the rest of the village.
 */
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { getIdleContent } from '@hedgerow/data';
import {
  idleDay,
  idleEventNow,
  idleNextDayAt,
  idleOrders,
  idleRosetteView,
  type IdleWorldState,
} from '@hedgerow/sim';
import {
  eventEnds,
  eventGoal,
  eventLine,
  orderLine,
  ordersFirstDay,
  ordersNext,
  orderTitle,
  rosetteLine,
  rosetteProgress,
  rosettesTitle,
} from './daily-view.ts';
import { coins, gain, num, useScientific } from './notation.ts';
import { flyCoins, playSound, popNumber } from '../juice/index.ts';
import { Button } from '../ui/Button.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { Figs } from './BuyButton.tsx';
import { hasSpriteIcon, Icon } from '../ui/Icon.tsx';
import { CountPill, SectionHead, TAB_BODY, useArrival, useArrive, useBadges } from './tab-ui.tsx';
import { newLine, ordersWaitingLine } from './badges.ts';
import { idleContent, useIdle } from './store.ts';
import { cap, t as term } from './terms.ts';
import { fmtSpan, goalLines, refusalText } from './view.ts';

const CARD: JSX.CSSProperties = {
  display: 'grid',
  gap: 8,
  padding: 10,
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
};

const SMALL: JSX.CSSProperties = {
  font: '400 var(--fs-caption)/1.4 var(--font-ui)',
  color: 'var(--ink-2)',
};

export function RegionTab() {
  const region = useIdle((s) => s.regionTable ?? s.region);
  const table = useIdle((s) => s.regionTable);
  const t = useIdle((s) => s.now);
  const world = useIdle((s) => s.world);
  useScientific();
  useEffect(() => {
    void useIdle.getState().loadRegion();
  }, []);
  // D460: opening the tab with something waiting scrolls to it and pulses its ring.
  const root = useRef<HTMLDivElement>(null);
  const badged = (useBadges()?.region.count ?? 0) > 0;
  const arrived = useArrival();
  useArrive(root, arrived && badged);
  if (!region)
    return (
      <div
        ref={root}
        data-screen-label={term('tabRegion')}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'grid',
          gap: 12,
          padding: 12,
          alignContent: 'start',
        }}
      >
        {world && <Village world={world} t={t} />}
        <span style={{ ...SMALL, textAlign: 'center' }}>Loading the {term('region')}</span>
      </div>
    );
  const g = region.goal;
  const share = g.target > 0 ? Math.min(1, g.earned / g.target) : 0;
  const lines = goalLines(region, t);
  const bonus = region.bonus && region.bonus.until > t ? region.bonus : undefined;
  const left = fmtSpan(Math.max(0, g.weekEndsAt - t));
  const mineShare =
    g.mine !== undefined && g.earned > 0 ? Math.round((g.mine / g.earned) * 100) : 0;
  return (
    <div ref={root} data-screen-label={term('tabRegion')} style={TAB_BODY}>
      {/* D431: the weekly goal is the hero: what the region has earned, of the target, a 10px
          bar, then the time left and what meeting it pays. */}
      <section
        style={{ ...CARD, gap: 6, borderRadius: 'var(--radius-lg)' }}
        data-testid="region-goal"
      >
        <span style={{ font: '600 var(--fs-caption)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}>
          <Figs text={`${term('weeklyGoal')} · ${left} left`} />
        </span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span class="num" style={{ font: '700 var(--fs-heading)/1.1 var(--font-num)' }}>
            {coins(g.earned)}
          </span>
          <span style={{ font: '500 var(--fs-label)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}>
            of{' '}
            <span class="num" style={{ font: '500 var(--fs-label)/1.2 var(--font-num)' }}>
              {coins(g.target)}
            </span>
          </span>
        </span>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(share * 100)}
          aria-label={term('weeklyGoal')}
          class="juice-bar"
          style={{ height: '6px', borderRadius: '4px' }}
        >
          <span
            style={{
              background: g.met ? 'var(--coin)' : 'var(--accent)',
              transform: `scaleX(${share})`,
            }}
          />
        </div>
        <span style={SMALL}>
          <Figs text={lines.reward} />
        </span>
      </section>
      {/* D442: one card of three figures: the bonus in force (green), your share, the members. */}
      <section
        style={{ ...CARD, gap: 6, borderRadius: 'var(--radius-lg)' }}
        data-testid="region-figures"
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${(bonus ? 1 : 0) + (g.mine !== undefined ? 1 : 0) + 1}, minmax(0, 1fr))`,
            gap: 8,
          }}
        >
          {bonus && (
            <Fig
              testId="region-bonus"
              label="Last week's goal met"
              value={`x${bonus.factor}`}
              color="var(--green)"
            />
          )}
          {g.mine !== undefined && (
            <Fig testId="region-share" label="Your share" value={`${mineShare}%`} />
          )}
          <Fig testId="region-members" label={cap(term('worlds'))} value={String(g.members)} />
        </div>
        {bonus && <span style={SMALL}>{`Bonus for ${fmtSpan(bonus.until - t)}.`}</span>}
      </section>
      {world && <Village world={world} t={t} />}
      <section style={{ display: 'grid', gap: 6 }} data-testid="region-worlds">
        <SectionHead title="This week" count={table ? table.worlds.length : undefined} />
        {!table ? (
          <span style={SMALL}>Loading the {term('worlds')}</span>
        ) : (
          // D442: one grouped list, 12px corners, rows of 48px ruled apart; your own is tinted.
          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              overflow: 'hidden',
            }}
          >
            {table.worlds.map((f, i) => (
              <div
                key={f.worldId}
                data-testid="region-world"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '32px minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 10,
                  height: 48,
                  boxSizing: 'border-box',
                  padding: '0 var(--edge)',
                  borderTop: i === 0 ? 'none' : '1px solid var(--line)',
                  ...(f.mine
                    ? { background: 'color-mix(in srgb, var(--accent) 8%, var(--surface))' }
                    : {}),
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--surface-2)',
                    color: 'var(--ink)',
                  }}
                >
                  <Icon name={worldTabIcon(f.theme)} size={18} />
                </span>
                <span style={{ display: 'grid', minWidth: 0 }}>
                  <span
                    style={{
                      font: '600 var(--fs-body)/1.25 var(--font-ui)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...(f.mine ? { color: 'var(--accent)' } : {}),
                    }}
                  >
                    {f.name}
                    {f.mine ? ' (you)' : ''}
                  </span>
                  <span
                    style={{
                      font: '500 var(--fs-note)/1.25 var(--font-ui)',
                      color: 'var(--ink-2)',
                    }}
                  >
                    <Figs text={`${term('heritage')} ${num(f.heritage)}`} />
                  </span>
                </span>
                <span class="num" style={{ font: '600 var(--fs-label)/1.2 var(--font-num)' }}>
                  {coins(f.weekEarned ?? 0)}
                </span>
              </div>
            ))}
          </div>
        )}
        <span style={{ ...SMALL, margin: '0 2px' }}>
          <Figs
            text={`${g.members} ${g.members === 1 ? `${term('world')} counts` : `${term('worlds')} count`} towards the goal.`}
          />
        </span>
      </section>
    </div>
  );
}

/** One of the three figures: a 11px label over a 20px mono value. */
function Fig({
  label,
  value,
  color = 'var(--ink)',
  testId,
}: {
  label: string;
  value: string;
  color?: string;
  testId: string;
}) {
  return (
    <div data-testid={testId} style={{ display: 'grid', gap: 4, alignContent: 'start' }}>
      <span style={{ font: '600 var(--fs-note)/1.2 var(--font-ui)', color: 'var(--ink-2)' }}>
        {label}
      </span>
      <span class="num" style={{ font: '700 var(--fs-title)/1.2 var(--font-num)', color }}>
        {value}
      </span>
    </div>
  );
}

/** D433: a member's own theme's world tab icon, for its circle in the table. */
function worldTabIcon(theme: string | undefined): string {
  const word = getIdleContent(theme).terms.tabWorld;
  const own = `tabs_${word.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
  return hasSpriteIcon(own) ? own : 'land-plot';
}

/** A thin progress bar, as the region goal's. */
function Bar({ share, done, label }: { share: number; done: boolean; label: string }) {
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(share * 100)}
      aria-label={label}
      class="juice-bar"
      style={{ height: '8px', borderRadius: '8px' }}
    >
      <span
        style={{
          background: done ? 'var(--coin)' : 'var(--accent)',
          transform: `scaleX(${Math.min(1, share)})`,
        }}
      />
    </div>
  );
}

/**
 * D304: the day's three village orders and the themed week. Orders are paid on the next
 * check-in once filled; the screen checks in as it polls, so a filled order shows as sent.
 */
function Village({ world, t }: { world: IdleWorldState; t: number }) {
  const orders = idleOrders(world, idleContent, t);
  // D460: the orders ready to send, as the tab's badge counts them.
  const waiting = useBadges()?.region;
  const marked = new Set(waiting?.items ?? []);
  const ordersWaiting = waiting?.cards['village-orders'] ?? 0;
  // D461: rosettes and the week's prize new since their sheets were last opened.
  const rosettesWaiting = waiting?.cards['village-rosettes'] ?? 0;
  const prizeWaiting = waiting?.cards['village-event'] ?? 0;
  const rosetteNames = (waiting?.waiting ?? [])
    .filter((w) => w.card === 'village-rosettes')
    .map((w) => idleContent.collection.rosettes.find((r) => r.id === w.items[0])?.name ?? '');
  const send = (i: number, el: Element) => {
    const r = useIdle.getState().act({ type: 'sendOrder', order: i });
    if (!r.ok) {
      playSound('error');
      popNumber(el, refusalText(idleContent, r));
      return;
    }
    playSound('coin', { combo: 4 });
    popNumber(el, gain(orders[i]?.coins ?? 0));
    flyCoins(el, { count: 8 });
  };
  const week = idleEventNow(world, idleContent, t);
  const rosettes = idleRosetteView(world, idleContent);
  const firstDay = idleDay(idleContent, world.createdAt) >= idleDay(idleContent, t);
  const [open, setOpen] = useState<'orders' | 'event' | 'rosettes' | null>(null);
  const close = () => setOpen(null);
  const sent = orders.filter((o) => o.done).length;
  return (
    <>
      <LinkCard
        testId="village-orders"
        icon="clipboard-list"
        title={term('ordersTitle')}
        value={
          orders.length === 0
            ? 'No orders today'
            : ordersWaiting > 0
              ? ordersWaitingLine(ordersWaiting)
              : `${sent} of ${orders.length} sent`
        }
        count={ordersWaiting}
        onOpen={() => setOpen('orders')}
      />
      <LinkCard
        testId="village-event"
        icon="sparkles"
        title={week.event.name}
        value={
          prizeWaiting > 0
            ? newLine([week.event.prize.name])
            : week.won
              ? 'Done'
              : `${week.progress} of ${week.goal}`
        }
        count={prizeWaiting}
        onOpen={() => {
          setOpen('event');
          useIdle.getState().seeNew('week');
        }}
      />
      <LinkCard
        testId="village-rosettes"
        icon="award"
        title={rosettesTitle()}
        value={rosettesWaiting > 0 ? newLine(rosetteNames) : String(rosettes.count)}
        count={rosettesWaiting}
        onOpen={() => {
          setOpen('rosettes');
          useIdle.getState().seeNew('rosettes');
        }}
      />
      <Sheet
        open={open === 'orders'}
        onClose={close}
        title={term('ordersTitle')}
        align="bottom"
        testId="village-orders-sheet"
      >
        <div style={{ display: 'grid', gap: 8 }}>
          {orders.length === 0 ? (
            <span style={SMALL}>{firstDay ? ordersFirstDay() : 'No orders today'}</span>
          ) : (
            orders.map((o, i) => (
              <div
                key={i}
                data-testid="village-order"
                class={marked.has(`order-${i}`) ? 'idle-waiting' : undefined}
                style={{
                  display: 'grid',
                  gap: 4,
                  padding: '4px 12px 10px',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
                  {marked.has(`order-${i}`) && (
                    <span
                      class="idle-waiting-dot"
                      aria-hidden="true"
                      style={{ position: 'static', flex: 'none', boxShadow: 'none' }}
                    />
                  )}
                  <span style={{ flex: 1, font: '500 var(--fs-body)/1.3 var(--font-ui)' }}>
                    {orderTitle(o)}
                  </span>
                  {o.ready && (
                    <span data-testid="village-order-send">
                      <Button
                        variant="primary"
                        onClick={(e) => send(i, e.currentTarget as Element)}
                      >
                        Send
                      </Button>
                    </span>
                  )}
                </div>
                <Bar
                  share={o.count > 0 ? o.made / o.count : 0}
                  done={o.done}
                  label={orderTitle(o)}
                />
                <span style={{ ...SMALL, color: o.done ? 'var(--green)' : SMALL.color }}>
                  <Figs text={orderLine(o)} />
                </span>
              </div>
            ))
          )}
          <span style={SMALL}>{ordersNext(idleNextDayAt(idleContent, t), t)}</span>
        </div>
      </Sheet>
      <Sheet
        open={open === 'event'}
        onClose={close}
        title={week.event.name}
        align="bottom"
        testId="village-event-sheet"
      >
        <div style={{ display: 'grid', gap: 8 }}>
          <span style={{ font: '500 var(--fs-body)/1.3 var(--font-ui)' }}>{eventGoal(week)}</span>
          <Bar share={week.progress / week.goal} done={week.won} label={eventGoal(week)} />
          <span style={{ ...SMALL, color: week.won ? 'var(--green)' : SMALL.color }}>
            {eventLine(week)}
          </span>
          <span style={SMALL}>{eventEnds(week, t)}</span>
        </div>
      </Sheet>
      <Sheet
        open={open === 'rosettes'}
        onClose={close}
        title={rosettesTitle()}
        align="bottom"
        testId="village-rosettes-sheet"
      >
        <div style={{ display: 'grid', gap: 8 }}>
          <span style={{ font: '500 var(--fs-body)/1.3 var(--font-ui)' }}>
            {rosetteLine(rosettes)}
          </span>
          <span style={SMALL}>{rosetteProgress(rosettes)}</span>
        </div>
      </Sheet>
    </>
  );
}

/**
 * A tappable row: a leading icon, one line, a chevron. 44px minimum, 12px corners. D460: with
 * something waiting inside (`count`), the accent ring and the count as a 22px pill before the
 * chevron, and the line says what waits.
 */
function LinkCard({
  testId,
  icon,
  title,
  value,
  count = 0,
  onOpen,
}: {
  testId: string;
  icon: string;
  title: string;
  value: string;
  count?: number;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      class={count > 0 ? 'idle-waiting' : undefined}
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 48,
        padding: '0 var(--edge)',
        textAlign: 'left',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        color: 'var(--ink)',
        cursor: 'pointer',
        font: '600 var(--fs-body)/1.3 var(--font-ui)',
      }}
    >
      <Icon name={icon} size={20} />
      <span style={{ flex: 1, minWidth: 0 }}>
        {title} <span style={{ ...SMALL, fontSize: 13 }}>{value}</span>
      </span>
      {count > 0 && <CountPill n={count} size={22} testId={`${testId}-badge`} />}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--ink-2)"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </button>
  );
}
